/**
 * Dashboard error reporting into our self-hosted GlitchTip.
 *
 * A file of our own: upstream kept this block duplicated across two entrypoints
 * (`entrypoints/dashboard.js` and `entrypoints/v3app.js`), and an entrypoint cannot
 * be imported under vitest — resolution dies on `vuex-router-sync`. Moving it here
 * removes the duplicate and makes the configuration testable.
 *
 * The DSN, the environment and the release arrive from the server through `window.*`
 * set in `app/views/layouts/vueapp.html.erb`. That beats deriving the environment from
 * the hostname (which is what the neighbouring flexar-frontend does): the same image is
 * promoted from the stand to production, and a host list would have to be kept in sync
 * with nginx's `server_name`. Here there is one source of truth — `SENTRY_ENVIRONMENT`
 * in the Rails environment.
 *
 * See flexar-docs/kb/adr/2026-08-29-glitchtip-error-reporting.md.
 */
import * as Sentry from '@sentry/vue';

const DENY_URLS = [
  // Chrome extensions
  /^chrome:\/\//i,
  /chrome-extension:/i,
  /extensions\//i,

  // Locally saved copies
  /file:\/\//i,

  // Safari extensions.
  /safari-web-extension:/i,
  /safari-extension:/i,
];

const IGNORE_ERRORS = [
  'ResizeObserver loop completed with undelivered notifications',
];

// `htmlTreeAsString` appends a whitelist of attributes to the selector it puts in a
// click breadcrumb, and three of them carry text an agent can see: a contact row spells
// the email into `title`, an avatar spells the name into `alt`.
const LABEL_ATTRIBUTES = /\[(?:title|alt|aria-label)="[^"]*"\]/g;

const withoutQuery = url => String(url).split(/[?#]/)[0];

/**
 * Share of traced requests. Garbage means "off", not "trace everything" — the same
 * degradation SENTRY_TRACES_SAMPLE_RATE has on the backend.
 */
export function resolveTracesSampleRate(raw) {
  const rate = Number.parseFloat(raw);
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
    return 0;
  }
  return rate;
}

/**
 * Breadcrumbs keep the shape of what happened, not what was in it.
 *
 * Request and navigation breadcrumbs record the URL verbatim, and in this dashboard the
 * query string is where the payload lives: contact search and message search both put the
 * agent's term in `q`, and message search runs against the body of the conversation.
 */
export function scrubBreadcrumb(breadcrumb) {
  const data = breadcrumb.data;
  if (data) {
    ['url', 'from', 'to'].forEach(key => {
      if (typeof data[key] === 'string') {
        data[key] = withoutQuery(data[key]);
      }
    });
  }
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = breadcrumb.message.replace(LABEL_ATTRIBUTES, '');
  }
  return breadcrumb;
}

/**
 * Start error reporting. Returns whether the SDK was armed: an empty DSN is a normal
 * state (local development), not a failure.
 */
export function initErrorReporting(app, router) {
  const dsn = window.errorLoggingConfig;
  if (!dsn) {
    return false;
  }

  const context = window.errorReportingContext || {};

  Sentry.init({
    app,
    dsn,
    environment: context.environment,
    release: context.release,
    tracesSampleRate: resolveTracesSampleRate(context.tracesSampleRate),
    // Everything an agent sees in the dashboard is customer data; neither an IP nor a
    // cookie belongs in the report. This is the @sentry/vue default already, but
    // upstream inverted it on the backend, so it is spelled out here.
    sendDefaultPii: false,
    denyUrls: DENY_URLS,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      // The Vue integration attaches the failing component's props by default, and
      // `Message` takes the message text as a first-level prop (`content`), so one render
      // error would carry the conversation into the report verbatim.
      Sentry.vueIntegration({ attachProps: false }),
      // A console breadcrumb's message is built from the `console.*` arguments, and
      // `data.arguments` keeps them serialised in full. In the dashboard `console.error`
      // is where failed API responses land: conversation text, contacts, tokens.
      Sentry.breadcrumbsIntegration({ console: false }),
    ],
    beforeBreadcrumb: scrubBreadcrumb,
    ignoreErrors: IGNORE_ERRORS,
  });

  return true;
}
